import { useState } from 'react';

const MASK_DOTS_MAX = 24;

export function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
  className,
  invalid,
  disabled,
  allowAutofill = false,
  toggleLabel = 'password',
  maskLength,
  maskHint
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  className?: string;
  invalid?: boolean;
  disabled?: boolean;
  allowAutofill?: boolean;
  toggleLabel?: string;
  maskLength?: number;
  maskHint?: string;
}): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const action = `${visible ? 'Hide' : 'Show'} ${toggleLabel}`;

  const [touched, setTouched] = useState(false);

  const [maskSeen, setMaskSeen] = useState(maskLength);
  if (maskLength !== maskSeen) {
    setMaskSeen(maskLength);
    if (touched) setTouched(false);
  }

  const showMask = maskLength !== undefined && maskLength > 0 && value === '' && !touched;

  const maskDisplay = visible
    ? (maskHint ?? '•'.repeat(Math.min(maskLength ?? 0, MASK_DOTS_MAX)))
    : '•'.repeat(Math.min(maskLength ?? 0, MASK_DOTS_MAX));

  function onMaskedChange(next: string): void {
    setTouched(true);
    if (!showMask) {
      onChange(next);
      return;
    }
    let typed = next;
    if (typed.includes(maskDisplay)) {
      typed = typed.replace(maskDisplay, '');
    } else {
      typed = typed.replace(/[•…]/g, '');
      if (maskHint !== undefined && typed.length > 0) {
        for (const part of maskHint.split('…')) {
          if (part.length > 0 && typed.startsWith(part)) typed = typed.slice(part.length);
          else if (part.length > 0 && typed.endsWith(part)) typed = typed.slice(0, -part.length);
        }
      }
    }
    onChange(typed);
  }

  return (
    <div className="password-wrap">
      <input
        id={id}
        className={`input${invalid ? ' invalid' : ''}${className ? ` ${className}` : ''}${
          showMask ? ' is-masked' : ''
        }`}
        type={showMask ? 'text' : visible ? 'text' : 'password'}
        placeholder={showMask ? '' : placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        readOnly={!allowAutofill && !focused}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        value={showMask ? maskDisplay : value}
        onChange={(e) => onMaskedChange(e.target.value)}
      />
      {(showMask || value !== '') && (
        <button
          type="button"
          className="password-toggle"
          aria-label={showMask ? (visible ? 'Hide credential hint' : 'Show credential hint') : action}
          aria-pressed={visible}
          title={showMask ? (visible ? 'Hide credential hint' : 'Show credential hint') : action}
          disabled={disabled}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}
    </div>
  );
}

function EyeIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
